/**
 * Bero UI primitives — the ONLY building blocks screens may use for
 * text, buttons, cards, pills, inputs, bars, sheets, lists, empty states.
 * All styling flows from @bero/tokens through useTheme(). No hex in screens.
 */
import React from 'react';
import {
  ActivityIndicator,
  Animated,
  Platform,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text as RNText,
  TextInput,
  View,
  useWindowDimensions,
  type PressableProps,
  type StyleProp,
  type TextInputProps,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { router } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fonts, radius, space, type as typeRamp, type PillStatus } from '@bero/tokens';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { useInShell } from '@/lib/in-shell';
import type { DictKey } from '@/i18n/en';
import { BeroMark, BrandLockup, Wordmark } from './brand';

export { BeroMark, BrandLockup, Wordmark };

/* ---------------- Text ---------------- */
type Variant = keyof typeof typeRamp;
type Tone = 'primary' | 'secondary' | 'tertiary' | 'onDark' | 'brand' | 'danger';

const fontForWeight: Record<string, string> = {
  '400': fonts.regular, '500': fonts.medium, '600': fonts.semibold, '700': fonts.bold,
};

export function AppText({
  variant = 'bodyMd', tone = 'primary', weight, mono, display, style, children, ...rest
}: {
  variant?: Variant; tone?: Tone; weight?: '400' | '500' | '600' | '700';
  /** Display voice (Sora) — reserved for the screen's few hero moments. */
  mono?: boolean; display?: boolean; style?: StyleProp<TextStyle>; children?: React.ReactNode;
} & React.ComponentProps<typeof RNText>) {
  const { colors } = useTheme();
  const ramp = typeRamp[variant];
  const w = weight ?? (ramp as { fontWeight: string }).fontWeight;
  const toneColor = {
    primary: colors.textPrimary, secondary: colors.textSecondary, tertiary: colors.textTertiary,
    onDark: colors.textOnDark, brand: colors.primary500, danger: colors.danger500,
  }[tone];
  return (
    <RNText
      {...rest}
      style={[
        { fontSize: ramp.fontSize, lineHeight: ramp.lineHeight, letterSpacing: ramp.letterSpacing, color: toneColor },
        'textTransform' in ramp ? { textTransform: ramp.textTransform } : null,
        { fontFamily: mono ? fonts.mono : display ? fonts.display : fontForWeight[w] },
        display ? { letterSpacing: ramp.fontSize >= 24 ? -0.8 : -0.4 } : null,
        mono ? { fontVariant: ['tabular-nums'] } : null,
        style,
      ]}
    >
      {children}
    </RNText>
  );
}

/* ---------------- Screen scaffold ----------------
 * Safe-area contract (project-wide, do not re-implement in screens):
 *  - TopBar owns the TOP inset. Screens WITHOUT a TopBar must pass safeTop.
 *  - Screen owns the BOTTOM inset (scroll padding / static padding) by default.
 *  - Sheet owns its own bottom inset. Never call useSafeAreaInsets in a screen.
 */
export function Screen({ children, scroll = true, padded = false, safeTop = false, safeBottom = true, fullWidth = false, style }: {
  children: React.ReactNode; scroll?: boolean; padded?: boolean;
  safeTop?: boolean; safeBottom?: boolean;
  /** Desktop web caps padded content at 960px centered (kills phone-stretch). Pass fullWidth for intentionally wide layouts. */
  fullWidth?: boolean; style?: StyleProp<ViewStyle>;
}) {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { width: winW } = useWindowDimensions();
  const capWidth = !fullWidth && padded && Platform.OS === 'web' && winW >= 1024;
  const cap: ViewStyle | null = capWidth ? { maxWidth: 960, width: '100%', alignSelf: 'center' } : null;
  const base: ViewStyle = {
    flex: 1,
    backgroundColor: colors.canvas0,
    paddingTop: safeTop ? insets.top : 0,
  };
  const bottomPad = safeBottom ? space.s6 + insets.bottom : space.s6;
  if (!scroll) {
    return (
      <View style={[base, padded && { paddingHorizontal: space.s4 }, safeBottom && { paddingBottom: insets.bottom }, cap, style]}>
        {children}
      </View>
    );
  }
  return (
    <View style={base}>
      <ScrollView
        contentContainerStyle={[padded && { paddingHorizontal: space.s4 }, { paddingBottom: bottomPad }, cap, style as ViewStyle]}
        showsVerticalScrollIndicator={false}
      >
        {children}
      </ScrollView>
    </View>
  );
}

/* ---------------- TopBar ----------------
 * Brand rule (user ask 2026-07-21): the full lockup — arch mark + "bero"
 * wordmark — appears in EVERY header, for one uniform brand presence across the
 * whole app. brand: 'lockup' (default) | 'mark' (mark only) | 'none' (screens
 * that render the lockup themselves, e.g. greeting bands).
 */
export function TopBar({ title, onBack, right, bordered = true, large, brand }: {
  title?: string; onBack?: () => void; right?: React.ReactNode; bordered?: boolean; large?: boolean;
  brand?: 'mark' | 'lockup' | 'none';
}) {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const inShell = useInShell();
  // Inside the desktop sidebar shell the sidebar already shows the lockup, so the
  // content TopBar drops its brand to avoid a redundant second "bero" and a
  // mismatched top divider. Explicit `brand` still wins.
  const brandMode = brand ?? (inShell ? 'none' : 'lockup');
  return (
    <View
      style={{
        paddingTop: insets.top,
        backgroundColor: colors.canvas0,
        borderBottomWidth: bordered ? StyleSheet.hairlineWidth : 0,
        borderBottomColor: colors.borderSoft,
      }}
    >
      <View style={{ height: 56, flexDirection: 'row', alignItems: 'center', paddingHorizontal: space.s2 }}>
        {onBack ? (
          <IconButton label="Back" onPress={onBack}>
            <AppText variant="headingLg" tone="primary">‹</AppText>
          </IconButton>
        ) : (
          <View style={{ width: space.s2 }} />
        )}
        {brandMode === 'lockup' ? (
          <Pressable accessibilityRole="link" accessibilityLabel="Bero home" onPress={() => router.push('/home')}
            style={{ paddingLeft: space.s2, paddingRight: space.s3 }}><BrandLockup markHeight={24} /></Pressable>
        ) : brandMode === 'mark' ? (
          <Pressable accessibilityRole="link" accessibilityLabel="Bero home" onPress={() => router.push('/home')}
            style={{ paddingLeft: onBack ? 0 : space.s2, paddingRight: space.s3 }}><BrandLockup markHeight={20} /></Pressable>
        ) : null}
        {large ? (
          <AppText variant="headingLg" style={{ flex: 1 }}>{title}</AppText>
        ) : (
          <AppText variant="bodyLg" weight="500" style={{ flex: 1 }} numberOfLines={1}>{title}</AppText>
        )}
        <View style={{ minWidth: 40, flexDirection: 'row', justifyContent: 'flex-end' }}>{right}</View>
      </View>
    </View>
  );
}

export function IconButton({ children, label, onPress, size = 40 }: {
  children: React.ReactNode; label: string; onPress?: () => void; size?: number;
}) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole="button"
      onPress={onPress}
      hitSlop={4}
      style={({ pressed }) => ({
        width: size, height: size, borderRadius: radius.full,
        alignItems: 'center', justifyContent: 'center',
        backgroundColor: pressed ? colors.canvas2 : 'transparent',
      })}
    >
      {children}
    </Pressable>
  );
}

/* ---------------- Buttons ---------------- */
export function Button({
  title, kind = 'primary', size = 'md', loading, disabled, style, ...rest
}: {
  title: string; kind?: 'primary' | 'secondary' | 'tertiary' | 'destructive';
  size?: 'md' | 'lg'; loading?: boolean; style?: StyleProp<ViewStyle>;
} & Omit<PressableProps, 'style'>) {
  const { colors } = useTheme();
  const pad = size === 'lg' ? { paddingVertical: space.s4, paddingHorizontal: space.s6 } : { paddingVertical: space.s3, paddingHorizontal: space.s5 };
  const bg = { primary: colors.primary500, secondary: 'transparent', tertiary: 'transparent', destructive: colors.danger500 }[kind];
  const pressedBg = { primary: colors.primary600, secondary: colors.canvas1, tertiary: colors.canvas1, destructive: colors.danger700 }[kind];
  const fg = { primary: colors.canvas0, secondary: colors.textPrimary, tertiary: colors.primary500, destructive: colors.canvas0 }[kind];
  return (
    <Pressable
      accessibilityRole="button"
      disabled={disabled || loading}
      {...rest}
      style={({ pressed }) => [
        {
          minHeight: 44, borderRadius: radius.md, alignItems: 'center', justifyContent: 'center',
          backgroundColor: pressed ? pressedBg : bg,
          borderWidth: kind === 'secondary' ? 1 : 0, borderColor: colors.borderFirm,
          opacity: disabled ? 0.4 : 1, flexDirection: 'row', gap: space.s2,
          ...pad,
        },
        style,
      ]}
    >
      {loading ? <ActivityIndicator color={fg} size="small" /> : null}
      <AppText variant="bodyMd" weight="500" style={{ color: fg }}>{title}</AppText>
    </Pressable>
  );
}

/* ---------------- Cards ---------------- */
export function Card({ children, filled, style, onPress }: {
  children: React.ReactNode; filled?: boolean; style?: StyleProp<ViewStyle>; onPress?: () => void;
}) {
  const { colors } = useTheme();
  const s: ViewStyle = {
    backgroundColor: filled ? colors.canvas1 : colors.canvas0,
    borderWidth: filled ? 0 : StyleSheet.hairlineWidth,
    borderColor: colors.borderSoft,
    borderRadius: radius.lg,
    padding: space.s4,
  };
  if (onPress) {
    return (
      <Pressable accessibilityRole="button" onPress={onPress}
        style={({ pressed }) => [s, pressed && { backgroundColor: colors.canvas1 }, style]}>
        {children}
      </Pressable>
    );
  }
  return <View style={[s, style]}>{children}</View>;
}

/* ---------------- Status pill ---------------- */
const PILL_LABEL: Record<PillStatus, DictKey> = {
  open: 'pill_open', progress: 'pill_progress', urgent: 'pill_urgent', resolved: 'pill_resolved',
  approved: 'pill_approved', pending: 'pill_pending', rejected: 'pill_rejected',
};

export function Pill({ status, label }: { status: PillStatus; label?: string }) {
  const { colors } = useTheme();
  const { t } = useT();
  const map: Record<PillStatus, { bg: string; fg: string }> = {
    open: { bg: colors.infoTint, fg: colors.info500 },
    progress: { bg: colors.warning50, fg: colors.warning700 },
    urgent: { bg: colors.danger50, fg: colors.danger700 },
    resolved: { bg: colors.success50, fg: colors.success700 },
    approved: { bg: colors.primary100, fg: colors.primary900 },
    pending: { bg: colors.canvas2, fg: colors.textTertiary },
    rejected: { bg: colors.danger50, fg: colors.danger700 },
  };
  const { bg, fg } = map[status];
  return (
    <View style={{ backgroundColor: bg, borderRadius: 10, paddingHorizontal: space.s2, paddingVertical: 2, alignSelf: 'flex-start' }}>
      <AppText variant="label" style={{ color: fg }}>{label ?? t(PILL_LABEL[status])}</AppText>
    </View>
  );
}

/* ---------------- Notice card (the flagship component) ---------------- */
export type NoticeKind = 'urgent' | 'event' | 'committee' | 'general';

export function NoticeCard({ kind, label, title, body, when, meta, onPress }: {
  kind: NoticeKind; label?: string; title: string; body?: string; when?: string; meta?: string; onPress?: () => void;
}) {
  const { colors } = useTheme();
  const edge = { urgent: colors.danger500, event: colors.accent500, committee: colors.primary500, general: 'transparent' }[kind];
  const labelColor = { urgent: colors.danger500, event: colors.accent700, committee: colors.primary700, general: colors.textTertiary }[kind];
  return (
    <Pressable accessibilityRole="button" onPress={onPress}
      style={({ pressed }) => ({
        backgroundColor: pressed ? colors.canvas1 : colors.canvas0,
        borderWidth: StyleSheet.hairlineWidth, borderColor: colors.borderSoft,
        borderLeftWidth: kind === 'general' ? StyleSheet.hairlineWidth : 4,
        borderLeftColor: kind === 'general' ? colors.borderSoft : edge,
        borderRadius: radius.lg, padding: space.s4,
      })}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: space.s2 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          {kind === 'urgent' ? <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: colors.danger500 }} /> : null}
          <AppText variant="label" style={{ color: labelColor }}>{label ?? kind.toUpperCase()}</AppText>
        </View>
        {when ? <AppText variant="bodySm" tone="tertiary">{when}</AppText> : null}
      </View>
      <AppText variant="headingSm" weight="500" style={{ marginTop: space.s1 }}>{title}</AppText>
      {body ? <AppText variant="bodyMd" tone="secondary" numberOfLines={2} style={{ marginTop: 6 }}>{body}</AppText> : null}
      {meta ? <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>{meta}</AppText> : null}
    </Pressable>
  );
}

/* ---------------- Avatar ---------------- */
export function Avatar({ name, size = 40 }: { name: string; size?: number }) {
  const { colors } = useTheme();
  const initials = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <View style={{
      width: size, height: size, borderRadius: radius.full,
      backgroundColor: colors.primary100, alignItems: 'center', justifyContent: 'center',
    }}>
      <AppText weight="600" style={{ color: colors.primary900, fontSize: size * 0.36, lineHeight: size * 0.5 }}>
        {initials}
      </AppText>
    </View>
  );
}

/* ---------------- Section label ---------------- */
export function SectionLabel({ children, style }: { children: React.ReactNode; style?: StyleProp<ViewStyle> }) {
  return (
    <View style={[{ marginBottom: 10 }, style]}>
      <AppText variant="label" tone="tertiary">{children}</AppText>
    </View>
  );
}

/* ---------------- List row + hairline ---------------- */
export function ListRow({ left, title, subtitle, right, onPress }: {
  left?: React.ReactNode; title: string; subtitle?: string; right?: React.ReactNode; onPress?: () => void;
}) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole={onPress ? 'button' : undefined}
      onPress={onPress}
      style={({ pressed }) => ({
        flexDirection: 'row', alignItems: 'center', gap: space.s3,
        paddingVertical: 14, paddingHorizontal: space.s4,
        backgroundColor: pressed && onPress ? colors.canvas1 : 'transparent',
      })}
    >
      {left}
      <View style={{ flex: 1, minWidth: 0 }}>
        <AppText variant="bodyMd">{title}</AppText>
        {subtitle ? <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{subtitle}</AppText> : null}
      </View>
      {right}
      {onPress ? <AppText variant="bodyLg" tone="tertiary">›</AppText> : null}
    </Pressable>
  );
}

export function Hairline({ inset = 0 }: { inset?: number }) {
  const { colors } = useTheme();
  return <View style={{ height: StyleSheet.hairlineWidth, backgroundColor: colors.borderSoft, marginLeft: inset }} />;
}

/* ---------------- Inputs ---------------- */
export function Field({ label, help, error, children }: {
  label?: string; help?: string; error?: string; children: React.ReactNode;
}) {
  const { colors } = useTheme();
  return (
    <View style={{ gap: 6 }}>
      {label ? <AppText variant="bodySm" weight="500" tone="secondary">{label}</AppText> : null}
      {children}
      {error ? (
        <AppText variant="bodySm" style={{ color: colors.danger500 }}>{error}</AppText>
      ) : help ? (
        <AppText variant="bodySm" tone="tertiary">{help}</AppText>
      ) : null}
    </View>
  );
}

/** forwardRef so a form can move focus between fields — e.g. Enter on the email
 * field focuses the password field instead of submitting half a form. */
export const Input = React.forwardRef<TextInput, {
  hasError?: boolean;
  /** Password fields: renders a Show/Hide toggle and manages secureTextEntry. */
  revealable?: boolean;
} & TextInputProps>(function Input({ hasError, revealable, style, ...rest }, ref) {
  const { colors } = useTheme();
  const { t } = useT();
  const [focused, setFocused] = React.useState(false);
  const [revealed, setRevealed] = React.useState(false);
  const field = (
    <TextInput
      ref={ref}
      placeholderTextColor={colors.textTertiary}
      {...rest}
      secureTextEntry={revealable ? !revealed : rest.secureTextEntry}
      onFocus={(e) => { setFocused(true); rest.onFocus?.(e); }}
      onBlur={(e) => { setFocused(false); rest.onBlur?.(e); }}
      style={[
        {
          minHeight: 44, borderRadius: radius.md, borderWidth: 1,
          borderColor: hasError ? colors.danger500 : focused ? colors.primary500 : colors.border,
          backgroundColor: colors.canvas0, color: colors.textPrimary,
          paddingHorizontal: space.s3, paddingVertical: 10,
          paddingRight: revealable ? 64 : space.s3,
          fontSize: 14, fontFamily: fonts.regular,
        },
        revealable ? { flex: 1 } : null,
        style,
      ]}
    />
  );
  if (!revealable) return field;
  return (
    <View style={{ position: 'relative', flexDirection: 'row', alignItems: 'center' }}>
      {field}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={revealed ? 'Hide password' : 'Show password'}
        onPress={() => setRevealed((r) => !r)}
        hitSlop={8}
        style={{ position: 'absolute', right: space.s3, paddingVertical: 6 }}
      >
        <AppText variant="bodySm" weight="600" tone="brand">{revealed ? t('hide') : t('show')}</AppText>
      </Pressable>
    </View>
  );
});

/* ---------------- Empty state ----------------
 * Carries a 5%-opacity watermark BeroMark in the bottom-right corner, per
 * the redesign v3 "Empty" frame (Universal States.dc.html). Purely additive
 * — the icon/title/body/cta API and layout are unchanged. */
export function EmptyState({ icon, title, body, cta, onCta }: {
  icon?: React.ReactNode; title: string; body?: string; cta?: string; onCta?: () => void;
}) {
  return (
    <View style={{ alignItems: 'center', paddingVertical: 64, paddingHorizontal: space.s6, position: 'relative', overflow: 'hidden' }}>
      <View pointerEvents="none" style={{ position: 'absolute', right: -46, bottom: -40, opacity: 0.05 }}>
        <BeroMark height={220} />
      </View>
      {icon}
      <AppText variant="headingSm" style={{ marginTop: space.s4, textAlign: 'center' }}>{title}</AppText>
      {body ? (
        <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2, textAlign: 'center', maxWidth: 280 }}>
          {body}
        </AppText>
      ) : null}
      {cta ? <Button title={cta} onPress={onCta} style={{ marginTop: space.s4 }} /> : null}
    </View>
  );
}

/* ---------------- Bottom sheet ---------------- */
export function Sheet({ visible, onClose, children }: {
  visible: boolean; onClose: () => void; children: React.ReactNode;
}) {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <Pressable accessibilityLabel="Close" onPress={onClose} style={{ flex: 1, backgroundColor: colors.scrim }} />
      <View style={{
        backgroundColor: colors.canvas0, borderTopLeftRadius: radius.xl, borderTopRightRadius: radius.xl,
        padding: space.s6, paddingBottom: space.s6 + insets.bottom,
      }}>
        <View style={{
          width: 36, height: 4, borderRadius: 2, backgroundColor: colors.border,
          alignSelf: 'center', marginBottom: space.s4,
        }} />
        {children}
      </View>
    </Modal>
  );
}

/* ---------------- Skeleton ---------------- */
export function Skeleton({ w = '100%', h = 16, r = radius.md, style }: {
  w?: number | `${number}%`; h?: number; r?: number; style?: StyleProp<ViewStyle>;
}) {
  const { colors } = useTheme();
  const [pulse] = React.useState(() => new Animated.Value(0.55));
  React.useEffect(() => {
    const loop = Animated.loop(Animated.sequence([
      Animated.timing(pulse, { toValue: 1, duration: 700, useNativeDriver: true }),
      Animated.timing(pulse, { toValue: 0.55, duration: 700, useNativeDriver: true }),
    ]));
    loop.start();
    return () => loop.stop();
  }, [pulse]);
  return (
    <Animated.View
      style={[{ width: w, height: h, borderRadius: r, backgroundColor: colors.canvas2, opacity: pulse }, style]}
    />
  );
}

/** Number that counts up on mount (800ms ease-out). Renders via a format
 * function so callers keep full control (formatINR, plain, percentages). */
export function CountUp({ value, format, duration = 800, ...textProps }: {
  value: number; format?: (n: number) => string; duration?: number;
} & React.ComponentProps<typeof AppText>) {
  const [display, setDisplay] = React.useState(0);
  React.useEffect(() => {
    if (!Number.isFinite(value)) return;
    const start = Date.now();
    let raf: ReturnType<typeof requestAnimationFrame> | null = null;
    const tick = () => {
      const t = Math.min(1, (Date.now() - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(value * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setDisplay(value);
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf !== null) cancelAnimationFrame(raf); };
  }, [value, duration]);
  return <AppText {...textProps}>{format ? format(display) : String(Math.round(display))}</AppText>;
}

/** Brief scale pop — wrap a reward moment (acknowledge, upvote, RSVP) and
 * bump `trigger` to play. */
export function Pop({ trigger, children }: { trigger: number | boolean; children: React.ReactNode }) {
  const [scale] = React.useState(() => new Animated.Value(1));
  const first = React.useRef(true);
  React.useEffect(() => {
    if (first.current) { first.current = false; return; }
    Animated.sequence([
      Animated.timing(scale, { toValue: 1.14, duration: 110, useNativeDriver: true }),
      Animated.spring(scale, { toValue: 1, friction: 4, useNativeDriver: true }),
    ]).start();
  }, [trigger, scale]);
  return <Animated.View style={{ transform: [{ scale }] }}>{children}</Animated.View>;
}

/* ---------------- Chip (filter) ---------------- */
export function Chip({ label, active, onPress, disabled }: { label: string; active?: boolean; onPress?: () => void; disabled?: boolean }) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled: !!disabled, selected: !!active }}
      disabled={disabled}
      onPress={onPress}
      style={{
        paddingHorizontal: space.s3, paddingVertical: 6, borderRadius: radius.full,
        backgroundColor: active ? colors.primary100 : colors.canvas1,
        borderWidth: StyleSheet.hairlineWidth, borderColor: active ? colors.primary400 : colors.borderSoft,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <AppText variant="bodySm" weight={active ? '500' : '400'}
        style={{ color: active ? colors.primary900 : colors.textSecondary }}>
        {label}
      </AppText>
    </Pressable>
  );
}
